"use client";

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Script from "next/script";
import { DateTime } from "luxon";
import { useEngagementTracking } from "@portal/app/_components/hooks/useEngagementTracking";
import { logger } from "@portal/app/_common/lib/logger";
import { setCookie, COOKIE_KEYS } from "@portal/app/_common/lib/headers";
import { isValidFacebookPixelId } from "@portal/app/_common/lib/trackingValidation";

import type {
  PopulatedPayloadFormFunnel,
  SuccessPageConfig,
  PayloadLegalLinksConfig,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import {
  checkDisqualification,
  type DisqualificationResult,
} from "@portal/app/_components/formFunnel/utils/checkDisqualification";
import { DisqualifiedStep } from "@portal/app/_components/formFunnel/results/DisqualifiedStep";
import { FormFunnelTrackingProvider } from "@portal/app/_components/formFunnel/FormFunnelTrackingProvider";
import { DynamicFormStep } from "@portal/app/_components/formFunnel/DynamicFormStep";
import { InterstitialStep } from "@portal/app/_components/formFunnel/InterstitialStep";
import { LoadingScreen } from "@portal/app/_components/formFunnel/LoadingScreen";
import {
  PreMatchHostedFlow,
  type PreMatchContactPayload,
} from "@portal/app/_components/formFunnel/preMatchHosted";
import {
  FUNNEL_PROGRESS,
  getQuestionProgress,
  moveQuestionProgress,
  updateProgress,
  type FunnelProgressUpdate,
  type ProgressDirection,
  type QuestionProgressState,
} from "@portal/app/_components/formFunnel/utils/funnelProgress";
import { FormFunnelPageLayout } from "@portal/app/_components/formFunnel/FormFunnelPageLayout";
import { buildFunnelSteps } from "@portal/app/_components/formFunnel/utils/buildFunnelSteps";
import { describeFunnelQuestionStep } from "@portal/app/_components/formFunnel/utils/flowStepDescriptors";
import { FormFunnelStepLayout } from "@portal/app/_components/formFunnel/FormFunnelStepLayout";
import {
  ResultScenarioRouter,
  type HostedCompletionContext,
  type MatchScenario,
} from "@portal/app/_components/formFunnel/results/ResultScenarioRouter";
import { SuccessPage } from "@portal/app/_components/formFunnel/results/SuccessPage";
import type { QuestionConfig } from "@portal/app/_components/formFunnel/FormFieldRenderer";
import {
  buildConcentrationAvailabilityVariables,
  useFormFunnelAvailabilityLazyQuery,
  useFormFunnelAvailabilityQuery,
  useFormFunnelMatch,
} from "@portal/app/_components/formFunnel/hooks/useFormFunnelQueries";
import { useFormFunnelTracking } from "@portal/app/_components/formFunnel/hooks/useFormFunnelTracking";
import { waitForJornayaLeadId } from "@portal/app/_components/hooks/useJornaya";
import { useFormFunnelAnalytics } from "@portal/app/_components/formFunnel/hooks/useFormFunnelAnalytics";
import { useIncrementalSave } from "@portal/app/_components/formFunnel/hooks/useIncrementalSave";
import { mapResponseToScenario } from "@portal/app/_components/formFunnel/utils/mapResponseToScenario";
import { normalizeFunnelAnswers } from "@portal/app/_components/formFunnel/utils/normalizeFunnelAnswers";
import { toRelativePath } from "@portal/app/_components/formFunnel/utils/urlUtils";
import { getResultsShownTrackingProperties } from "@portal/app/_components/formFunnel/utils/getResultsShownTrackingProperties";
import { getOrCreateFormFunnelSessionId } from "@portal/app/_components/formFunnel/utils/formFunnelSessionStorage";
import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import {
  isQuestionVisible,
  evaluateConditionalRules,
} from "@portal/app/_components/formFunnel/utils/conditionalVisibility";
import type { FunnelExperimentAttribution } from "@portal/app/_server/services/formFunnelExperiment/types";
import type { VocationIndustry } from "@portal/app/_common/types/vocationIndustry";
import {
  filterConcentrationOptionsByAvailability,
  getZipAnswerFromFormAnswers,
  hasScrubbableConcentrationAnswer,
  isDynamicConcentrationQuestion,
  removeUnavailableQuestionAnswers,
  shouldFetchAvailability,
  type ConcentrationAvailability,
} from "@portal/app/_common/lib/formFunnelDynamicOptions";

type FlowState =
  | "questions"
  | "loading"
  | "preMatchHosted"
  | "results"
  | "disqualified"
  | "success";

/** Session expiration time in milliseconds (30 minutes) */
const SESSION_EXPIRATION_MS = 30 * 60 * 1000;

interface PersistedFunnelState {
  currentStep: number;
  formAnswers: Record<string, unknown>;
  timestamp: number;
}

/**
 * Loads persisted funnel state from sessionStorage.
 * Returns null if no state exists or if the state has expired (30 min inactivity).
 */
const loadPersistedState = (slug: string): PersistedFunnelState | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(`funnel_state_${slug}`);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as PersistedFunnelState;
    // Expire after 30 minutes of inactivity
    if (DateTime.now().toMillis() - parsed.timestamp > SESSION_EXPIRATION_MS) {
      sessionStorage.removeItem(`funnel_state_${slug}`);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Persists funnel state to sessionStorage with a timestamp.
 */
const persistState = (slug: string, currentStep: number, formAnswers: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  const state: PersistedFunnelState = {
    currentStep,
    formAnswers,
    timestamp: DateTime.now().toMillis(),
  };
  sessionStorage.setItem(`funnel_state_${slug}`, JSON.stringify(state));
};

/**
 * Clears persisted funnel state from sessionStorage.
 */
const clearPersistedState = (slug: string) => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(`funnel_state_${slug}`);
};

interface FormFunnelPageProps {
  funnel: PopulatedPayloadFormFunnel;
  legalLinks: PayloadLegalLinksConfig | null;
  trustBadgeText: string;
  /** When true, displays preview banner and disables lead submission */
  isPreview?: boolean;
  /** Experiment attribution for any active A/B test bucketing this render. */
  experimentAttribution?: FunnelExperimentAttribution[];
  /** True when the visitor was freshly assigned to a variant on this request. */
  isFreshAssignment?: boolean;
  /** Human-readable name of the active experiment, if any. */
  experimentName?: string;
  /** Whether this linked program keeps the mount-time marketplace event. */
  createMarketplaceEventOnMount?: boolean;
}

const FUNNEL_BANNER_HEIGHT = 64;

/** Admin warning banner displayed at top of page */
const AdminBanner = () => (
  <Box
    data-testid="form-funnel-admin-banner"
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      bgcolor: "error.main",
      color: "error.contrastText",
      py: 1,
      px: 2,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: `${FUNNEL_BANNER_HEIGHT}px`,
      boxSizing: "border-box",
    }}
  >
    <Typography variant="body2" fontWeight="bold">
      You are logged in as an admin. Use another browser or incognito session for testing.
    </Typography>
  </Box>
);

/** Preview mode banner displayed at top of page */
const PreviewBanner = ({ slug }: { slug: string }) => (
  <Box
    data-testid="form-funnel-preview-banner"
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      bgcolor: "warning.main",
      color: "warning.contrastText",
      py: 1,
      px: 2,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 2,
      minHeight: `${FUNNEL_BANNER_HEIGHT}px`,
      boxSizing: "border-box",
    }}
  >
    <Typography variant="body2" fontWeight="bold">
      PREVIEW MODE - This form funnel is not published
    </Typography>
    <Button
      variant="outlined"
      size="small"
      href={`/api/preview/exit?return=/flows/${slug}`}
      sx={{ color: "inherit", borderColor: "inherit" }}
    >
      Exit Preview
    </Button>
  </Box>
);

const FormFunnelContent = ({
  funnel,
  legalLinks,
  trustBadgeText,
  isPreview = false,
}: FormFunnelPageProps) => {
  const {
    customerId,
    role: { isAdmin },
  } = useAuthenticatedContext();
  const { trackEvent, trackStepComplete, trackFlowStep, getTrackingIds } = useFormFunnelTracking();
  const jornayaMatchWait = useRef<Promise<string | null> | null>(null);
  const bannerOffset = isPreview || isAdmin ? FUNNEL_BANNER_HEIGHT : 0;

  const [flowState, setFlowState] = useState<FlowState>("questions");
  const flowStateRef = useRef(flowState);
  flowStateRef.current = flowState;
  const [currentStep, setCurrentStep] = useState(0);
  const questionNavigationRef = useRef({ step: 0, generation: 0 });
  questionNavigationRef.current.step = currentStep;
  const questionGeneration = questionNavigationRef.current.generation;
  const [formAnswers, setFormAnswers] = useState<Record<string, unknown>>({});
  const [matchScenario, setMatchScenario] = useState<MatchScenario | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const hydratedFunnelRef = useRef<{ id: string; slug: string } | null>(null);
  const [disqualificationResult, setDisqualificationResult] =
    useState<DisqualificationResult | null>(null);
  const [successData, setSuccessData] = useState<{
    programName: string;
    providerName: string;
    providerLogoUrl?: string;
    providerSuccessMessage?: string | null;
    industry?: VocationIndustry;
    hasFinancialAid: boolean;
    firstName?: string;
    successPageConfig?: SuccessPageConfig;
    redirectUrl?: string;
    redirectDelaySeconds?: number;
  } | null>(null);
  const [questionProgress, setQuestionProgress] = useState<QuestionProgressState>({
    completedSteps: [],
    percent: 0,
  });
  const [hostedProgress, setHostedProgress] = useState<number>(FUNNEL_PROGRESS.results);
  const [preMatchProgress, setPreMatchProgress] = useState<number>(
    FUNNEL_PROGRESS.questionsComplete
  );
  /**
   * True once the user has finished pre-match contact collection — drives the
   * second-pass loading message ("Verifying your match…") so the screen
   * frames the wait as confirmation, not a fresh search. ENG-6248.
   */
  const [postPreMatchContact, setPostPreMatchContact] = useState(false);

  // Ref to hold the latest formAnswers, avoiding stale closure in handleStepComplete
  // during rapid auto-advance (radio/pictureChoice questions that auto-submit after 300ms).
  const formAnswersRef = useRef(formAnswers);
  formAnswersRef.current = formAnswers;
  const hasTrackedResults = useRef(false);
  const hasSavedSessionLanding = useRef(false);

  // GraphQL hook for fetching matches
  const { getMatches } = useFormFunnelMatch();
  // Imperative availability read for the submit-time backstop scrub below.
  const [getAvailableConcentrations] = useFormFunnelAvailabilityLazyQuery();
  const [formFunnelSessionId, setFormFunnelSessionId] = useState<string | undefined>();
  useFormFunnelAnalytics({
    formFunnelId: funnel.id,
    funnelSlug: funnel.slug,
    formFunnelSessionId,
    isAdmin: !!isAdmin,
    isPreview,
  });
  const { saveAnswer, saveSessionAnswers } = useIncrementalSave({
    sessionId: formFunnelSessionId,
    funnelId: funnel.id,
  });

  useEffect(() => {
    if (!isHydrated || !customerId || !formFunnelSessionId || hasSavedSessionLanding.current)
      return;
    hasSavedSessionLanding.current = true;
    void saveSessionAnswers({});
  }, [customerId, formFunnelSessionId, isHydrated, saveSessionAnswers]);

  // "Form Funnel - Results Shown" — fires when results appear
  useEffect(() => {
    if (flowState === "results" && matchScenario && !hasTrackedResults.current) {
      hasTrackedResults.current = true;
      trackEvent("Form Funnel - Results Shown", getResultsShownTrackingProperties(matchScenario));
    }
  }, [flowState, matchScenario, trackEvent]);

  // Persist state to sessionStorage whenever step or answers change
  useEffect(() => {
    if (isHydrated && flowState === "questions") {
      persistState(funnel.slug, currentStep, formAnswers);
    }
  }, [isHydrated, flowState, currentStep, formAnswers, funnel.slug]);

  const funnelSteps = useMemo(
    () => buildFunnelSteps({ questions: funnel.questions, questionConfig: funnel.questionConfig }),
    [funnel.questions, funnel.questionConfig]
  );

  const totalSteps = funnelSteps.length;

  /**
   * Returns true if the step at the given index is visible.
   * Interstitial steps evaluate their conditional rules (visible by default if none configured).
   * Question steps are visible if any question passes conditional logic.
   */
  const isStepVisible = useCallback(
    (stepIndex: number, answers: Record<string, unknown>) => {
      const step = funnelSteps[stepIndex];
      if (!step) return false;
      if (step.type === "interstitial")
        return evaluateConditionalRules(step.source.conditionalRules, answers);
      return step.questions.some((q) => isQuestionVisible(q, answers));
    },
    [funnelSteps]
  );

  /**
   * Finds the next visible step in the given direction starting from (but not including) fromStep.
   * Returns the step index, or -1 if no visible step is found.
   * Accepts an explicit answers map to evaluate visibility with the latest answers.
   */
  const findNextVisibleStep = useCallback(
    (
      fromStep: number,
      direction: "forward" | "backward",
      answers: Record<string, unknown>
    ): number => {
      const delta = direction === "forward" ? 1 : -1;
      let step = fromStep + delta;
      while (step >= 0 && step < totalSteps) {
        if (isStepVisible(step, answers)) return step;
        step += delta;
      }
      return -1;
    },
    [totalSteps, isStepVisible]
  );

  // Compute visible steps for accurate progress bar display.
  // Uses isStepVisible to evaluate both interstitial conditional rules and question conditional logic.
  const visibleStepIndices = useMemo(() => {
    return funnelSteps
      .map((_, index) => index)
      .filter((index) => isStepVisible(index, formAnswers));
  }, [funnelSteps, formAnswers, isStepVisible]);

  const visibleCurrentStepIndex = visibleStepIndices.indexOf(currentStep);

  // Restore the known questionnaire path after hydration. Completed screens stay
  // counted during this attempt even when a later answer changes their visibility.
  useEffect(() => {
    // A same-funnel server refresh may replace question/config arrays. Restore
    // once per funnel identity so it cannot erase answers after matching cleared storage.
    if (
      hydratedFunnelRef.current?.id === funnel.id &&
      hydratedFunnelRef.current.slug === funnel.slug
    ) {
      return;
    }
    hydratedFunnelRef.current = { id: funnel.id, slug: funnel.slug };
    const persisted = loadPersistedState(funnel.slug);
    const restoredStep = persisted?.currentStep ?? 0;
    const restoredAnswers = persisted?.formAnswers ?? {};
    const visibleSteps = funnelSteps
      .map((_, index) => index)
      .filter((index) => isStepVisible(index, restoredAnswers));
    const completedSteps = visibleSteps.filter((index) => index < restoredStep);
    questionNavigationRef.current = {
      step: restoredStep,
      generation: questionNavigationRef.current.generation + 1,
    };
    setCurrentStep(restoredStep);
    setFormAnswers(restoredAnswers);
    setQuestionProgress({
      completedSteps,
      percent: visibleSteps.length
        ? getQuestionProgress(
            completedSteps.length,
            visibleSteps.filter((index) => index >= restoredStep).length
          )
        : 0,
    });
    setFormFunnelSessionId(getOrCreateFormFunnelSessionId(funnel.id));
    setIsHydrated(true);
  }, [funnel.id, funnel.slug, funnelSteps, isStepVisible]);

  const navigateQuestionStep = useCallback(
    (
      nextStep: number,
      answers: Record<string, unknown>,
      direction: ProgressDirection,
      completeCurrentStep = true
    ) => {
      const remainingScreens = funnelSteps.filter(
        (_, index) => index >= nextStep && isStepVisible(index, answers)
      ).length;
      setQuestionProgress((previous) =>
        moveQuestionProgress(
          previous,
          currentStep,
          nextStep,
          remainingScreens,
          direction,
          completeCurrentStep
        )
      );
      questionNavigationRef.current = {
        step: nextStep,
        generation: questionNavigationRef.current.generation + 1,
      };
      setCurrentStep(nextStep);
    },
    [currentStep, funnelSteps, isStepVisible]
  );
  const firstVisibleQuestionStepIndex = visibleStepIndices.find(
    (stepIndex) => funnelSteps[stepIndex]?.type === "question"
  );
  const currentFunnelStep = funnelSteps[currentStep];
  const currentQuestionStep = currentFunnelStep?.type === "question" ? currentFunnelStep : null;
  // "Hidden" is only trustworthy for cross-step dependencies: DynamicFormStep
  // reveals questions from live Formik values, which land in formAnswers only
  // on step submit. A question whose dependency is a sibling in its own step
  // can therefore be revealed mid-step while formAnswers still says hidden —
  // fail open (treat it as visible) so the reveal finds a warm availability
  // cache instead of rendering unfiltered options the backstop would then
  // silently scrub.
  const isQuestionVisibleOrSameStepRevealable = (
    question: QuestionConfig,
    stepQuestions: readonly QuestionConfig[]
  ) =>
    isQuestionVisible(question, formAnswers) ||
    (!!question.conditionalLogic?.dependsOn &&
      stepQuestions.some((sibling) => sibling.id === question.conditionalLogic?.dependsOn));

  // Only a concentration question the user could actually see counts,
  // mirroring shouldFetchAvailability — one hidden by an earlier step's answer
  // (hidden step, or hidden inside a visible page group) is never rendered, so
  // it must not hold the step behind the loader.
  const hasCurrentDynamicConcentrationQuestion = currentQuestionStep
    ? currentQuestionStep.questions.some(
        (question) =>
          isQuestionVisibleOrSameStepRevealable(question, currentQuestionStep.questions) &&
          isDynamicConcentrationQuestion(question)
      )
    : false;
  // Fetch only once the next visible answer-collecting step contains a visible
  // concentration question — see shouldFetchAvailability (ENG-7917).
  const isAvailabilityNeededNow = shouldFetchAvailability(
    funnelSteps,
    currentStep,
    (index) => isStepVisible(index, formAnswers),
    (question, stepIndex) => {
      const step = funnelSteps[stepIndex];
      return (
        step.type === "question" && isQuestionVisibleOrSameStepRevealable(question, step.questions)
      );
    }
  );
  const dynamicOptionsZipAnswer = useMemo(
    () => getZipAnswerFromFormAnswers(funnel.questions, formAnswers),
    [funnel.questions, formAnswers]
  );
  // The submit-time backstop below builds its variables with the same shared
  // builder, so both reads hit the same Apollo cache key.
  const availabilityVariables = useMemo(
    () => buildConcentrationAvailabilityVariables(funnel.id, funnel.questions, formAnswers),
    [funnel.id, funnel.questions, formAnswers]
  );

  const skipAvailability =
    flowState !== "questions" ||
    !isHydrated ||
    !isAvailabilityNeededNow ||
    !dynamicOptionsZipAnswer;

  // Availability is read declaratively: Apollo caches by variables, so returning
  // to a step we've already resolved is instant. The request starts as soon as a
  // concentration question is the next answer-collecting step, and the step below
  // blocks while it is still loading, so the concentration list is never shown
  // unfiltered (which would let a user submit an unavailable subindustry).
  const {
    data: availabilityData,
    error: availabilityError,
    loading: availabilityLoading,
  } = useFormFunnelAvailabilityQuery({
    variables: availabilityVariables,
    skip: skipAvailability,
  });

  useEffect(() => {
    if (availabilityError) {
      logger.warn(availabilityError, "Failed to load form funnel dynamic concentration options");
    }
  }, [availabilityError]);
  // Both concentration facets come from the same query: subindustry-keyed
  // questions filter on `subindustries`, vocation-keyed (`program_vocation_in`)
  // questions filter on `vocationIds`.
  const availability = useMemo<ConcentrationAvailability>(() => {
    if (skipAvailability) return { subindustries: null, vocationIds: null };
    const result = availabilityData?.getFormFunnelAvailableConcentrations;
    return {
      subindustries: result?.subindustries ?? null,
      vocationIds: result?.vocationIds ?? null,
    };
  }, [skipAvailability, availabilityData]);
  // Block a concentration step only while its availability is genuinely in
  // flight — the prefetch (see shouldFetchAvailability) usually resolves it
  // before the step renders.
  const isLoadingDynamicOptions = hasCurrentDynamicConcentrationQuestion && availabilityLoading;

  const allDynamicConcentrationQuestions = useMemo(
    () =>
      funnelSteps
        .flatMap((step) => (step.type === "question" ? step.questions : []))
        .filter(isDynamicConcentrationQuestion)
        .map((question) => filterConcentrationOptionsByAvailability(question, availability)),
    [funnelSteps, availability]
  );
  const currentDynamicQuestions = useMemo(() => {
    if (!currentQuestionStep) return [];
    return currentQuestionStep.questions.map((question) =>
      filterConcentrationOptionsByAvailability(question, availability)
    );
  }, [currentQuestionStep, availability]);
  // Strip any saved concentration answer that is no longer available once
  // availability for the current answers resolves (e.g. the user changed ZIP).
  const currentDynamicAnswers = useMemo(
    () => removeUnavailableQuestionAnswers(allDynamicConcentrationQuestions, formAnswers),
    [allDynamicConcentrationQuestions, formAnswers]
  );
  const analyticsStep = funnelSteps[currentStep];
  const engagement =
    isHydrated &&
    formFunnelSessionId &&
    !isAdmin &&
    !isPreview &&
    flowState === "questions" &&
    !isLoadingDynamicOptions &&
    analyticsStep &&
    isStepVisible(currentStep, formAnswers)
      ? {
          stepId:
            analyticsStep.type === "interstitial"
              ? `interstitial:${analyticsStep.source.id}`
              : analyticsStep.questions.map((question) => question.id).join("|"),
          stepIndex: currentStep,
          formFunnelId: funnel.id,
          formFunnelSessionId,
        }
      : null;
  useEngagementTracking(
    engagement && analyticsStep?.type === "interstitial"
      ? { ...engagement, scope: "step", phase: "questions", fieldKeys: [] }
      : null
  );
  useEffect(() => {
    if (currentDynamicAnswers !== formAnswers) {
      setFormAnswers(currentDynamicAnswers);
    }
  }, [currentDynamicAnswers, formAnswers]);

  // Backstop run against the *final* answers just before matching. The live query
  // above stops once the user is past the concentration step, so a downstream
  // filter answer (education, environment, payment) could narrow the chosen
  // concentration to zero programs after the step-time scrub last ran. This
  // re-checks availability for all final answers and strips a now-unavailable
  // concentration answer so it never reaches the real DreamSearch match.
  // Fail-open on empty/error.
  //
  // It stays off the wire whenever it cannot change the outcome: no scrubbable
  // concentration answer at all, or an answer set the live query already resolved
  // (identical variables — an Apollo cache hit, no request).
  const scrubUnavailableConcentrationAnswers = useCallback(
    async (answers: Record<string, unknown>): Promise<Record<string, unknown>> => {
      const zipAnswer = getZipAnswerFromFormAnswers(funnel.questions, answers);
      const concentrationQuestions = funnelSteps
        .flatMap((step) => (step.type === "question" ? step.questions : []))
        .filter(isDynamicConcentrationQuestion);
      if (!zipAnswer || !concentrationQuestions.length) return answers;
      if (!hasScrubbableConcentrationAnswer(concentrationQuestions, answers)) return answers;

      try {
        const { data } = await getAvailableConcentrations({
          variables: buildConcentrationAvailabilityVariables(funnel.id, funnel.questions, answers),
        });
        const result = data?.getFormFunnelAvailableConcentrations;
        const finalAvailability: ConcentrationAvailability = {
          subindustries: result?.subindustries ?? [],
          vocationIds: result?.vocationIds ?? [],
        };
        if (!finalAvailability.subindustries?.length && !finalAvailability.vocationIds?.length) {
          return answers;
        }

        const filteredConcentrationQuestions = concentrationQuestions.map((question) =>
          filterConcentrationOptionsByAvailability(question, finalAvailability)
        );
        return removeUnavailableQuestionAnswers(filteredConcentrationQuestions, answers);
      } catch (error) {
        logger.warn(error, "Failed to sanitize form funnel concentration answers before matching");
        return answers;
      }
    },
    [funnel.id, funnel.questions, funnelSteps, getAvailableConcentrations]
  );

  // Build merged questions for HostedResult (UI questions + customer-mapped questions).
  // Customer-mapped questions may include hosted-step questions (IS_US_CITIZEN, etc.)
  // that are NOT linked to the funnel via form_funnels_rels but are needed by
  // HostedResult's cmsQuestionMap for resolving customer field mappings.
  const allQuestionsForHosted = useMemo(() => {
    if (!funnel.customerMappedQuestions?.length) return funnel.questions;
    const existingIds = new Set(funnel.questions.map((q) => q.id));
    const additional = funnel.customerMappedQuestions.filter((q) => !existingIds.has(q.id));
    return [...funnel.questions, ...additional];
  }, [funnel.questions, funnel.customerMappedQuestions]);

  const handleGetMatches = useCallback(
    async (answers: Record<string, unknown>, contactData?: PreMatchContactPayload) => {
      // Clear persisted state — by the time we're calling matches the user has
      // completed the questionnaire. If they refresh mid-pre-match-contact
      // they restart from the beginning, which is acceptable given session
      // expiration is already 30 minutes.
      clearPersistedState(funnel.slug);

      // First-pass call resets the "verifying" flag so a user who completes
      // the funnel a second time (e.g., after a disqualification → change
      // answer) sees the correct "Finding your match…" copy. Only the
      // second pass (when contactData is supplied) keeps the flag true.
      if (contactData === undefined) {
        setPostPreMatchContact(false);
        setPreMatchProgress(FUNNEL_PROGRESS.questionsComplete);
      }
      // A safe results baseline exists before either direct hosted or chooser
      // effects can report their effective steps. Previous selections cannot leak.
      setHostedProgress(FUNNEL_PROGRESS.results);

      const fallbackRedirectUrl = funnel.resultScenarios?.noMatchRedirectUrl || "/explore";

      // Auto-redirect genuine NO_MATCH results straight to DreamSearch, skipping
      // the NoMatchResult page entirely. Returns true if it redirected so callers
      // know to short-circuit. Preview mode is excluded so funnel builders can
      // still inspect the NoMatchResult page when testing.
      const redirectIfRealNoMatch = (scenario: MatchScenario, isErrorFallback = false): boolean => {
        if (scenario.type !== "NO_MATCH" || isPreview) return false;
        const searchUrl = toRelativePath(scenario.searchUrl || "/explore");
        trackEvent(
          "Form Funnel - Results Shown",
          getResultsShownTrackingProperties(scenario, isErrorFallback)
        );
        trackEvent("Form Funnel - No Match - Auto Redirected", { destinationUrl: searchUrl });
        // replace() (not href) so Back skips the now-empty funnel page —
        // clearPersistedState above already wiped the user's saved answers.
        window.location.replace(searchUrl);
        return true;
      };

      try {
        // In preview mode, skip real matching and show mock results immediately
        if (isPreview) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          setMatchScenario({
            type: "NO_MATCH",
            searchUrl: fallbackRedirectUrl,
          });
          setFlowState("results");
          return;
        }

        // Backstop: strip unavailable concentrations from matching only. Keep
        // formAnswers unchanged so session state and lead submission retain the
        // values the user actually selected.
        const answersForMatching = await scrubUnavailableConcentrationAnswers(answers);

        // Call the GraphQL query to get matches
        // Note: Geo extraction is handled server-side in processFormAnswers based on
        // the filterKey configuration (keys matching GEO_KEYS like "zip", "geo_zip").
        // The backend's geoOverrides are merged with this geo parameter, so we pass
        // an empty geoCenter and let the backend populate it from the form answers.
        // Give a late Jornaya token at most one extra second under the existing
        // loading screen. Reuse the wait across contact/retry passes to avoid
        // stacking delays; always read the current token first.
        // `everflowSub1` lets the server resolve PEC's WebInitiatingURL in
        // pre-ping; funnels whose provider doesn't map it ignore the value.
        const { jornayaLeadId: currentJornayaLeadId, everflowSub1 } = await getTrackingIds();
        const jornayaLeadId =
          currentJornayaLeadId ||
          (await (jornayaMatchWait.current ??= waitForJornayaLeadId({ timeoutMs: 1000 })));

        const { data, error } = await getMatches({
          variables: {
            funnelId: funnel.id,
            answers: answersForMatching,
            geo: { geoCenter: {} },
            jornayaLeadId: jornayaLeadId ?? undefined,
            contactData: contactData ?? undefined,
            sub1: everflowSub1 ?? undefined,
          },
        });

        if (error) {
          throw error;
        }

        const matchResult = data?.getFormFunnelMatches;
        if (matchResult?.needsContact) {
          // Server signaled the top candidate needs verified contact info
          // before the pre-ping loop fires. Pause here and let
          // PreMatchHostedFlow collect first/last/email/verified-phone.
          // ENG-6248.
          setFlowState("preMatchHosted");
          return;
        }

        if (!matchResult) {
          // No result from server, show NO_MATCH
          const noMatchScenario: MatchScenario = {
            type: "NO_MATCH",
            searchUrl: fallbackRedirectUrl,
          };
          if (redirectIfRealNoMatch(noMatchScenario)) return;
          setMatchScenario(noMatchScenario);
        } else {
          // Map the response to the appropriate scenario
          const scenario = mapResponseToScenario(
            matchResult,
            fallbackRedirectUrl,
            funnel.hostedFlowConfig,
            allQuestionsForHosted,
            funnel.programHighlights
          );
          if (redirectIfRealNoMatch(scenario, matchResult.isErrorFallback ?? false)) return;
          setMatchScenario(scenario);
        }

        setFlowState("results");
      } catch (error) {
        // Network/GraphQL failures fall through to the NoMatchResult fallback —
        // we deliberately do NOT auto-redirect here, so a transient error never
        // turns into a silent "no programs match you" outcome.
        logger.error(error, `Error getting matches for funnel ${funnel.id}`);
        setMatchScenario({
          type: "NO_MATCH",
          searchUrl: fallbackRedirectUrl,
        });
        setFlowState("results");
      }
    },
    [
      funnel,
      isPreview,
      getMatches,
      scrubUnavailableConcentrationAnswers,
      allQuestionsForHosted,
      trackEvent,
      getTrackingIds,
    ]
  );

  const handlePreMatchContactComplete = useCallback(
    (payload: PreMatchContactPayload) => {
      // Re-issue the matches query with the verified identity fields now
      // populated. The server will run the pre-ping loop on the same ranked
      // list and return the final scenario. ENG-6248.
      setPostPreMatchContact(true);
      setFlowState("loading");
      void handleGetMatches(formAnswers, payload);
    },
    [formAnswers, handleGetMatches]
  );

  // Auto-skip steps where all questions are conditionally hidden.
  // This handles two scenarios:
  // 1. Hydration lands on a step that is now hidden (persisted state from prior session)
  // 2. Forward navigation lands on a hidden step (handled inline in handleStepComplete,
  //    but this effect is a safety net for edge cases like rapid state changes)
  // If ALL remaining steps are hidden, triggers matching instead of showing an empty form.
  useEffect(() => {
    if (flowState !== "questions" || !isHydrated || totalSteps === 0) return;
    if (isStepVisible(currentStep, formAnswers)) return;

    const nextVisible = findNextVisibleStep(currentStep, "forward", formAnswers);
    if (nextVisible >= 0) {
      navigateQuestionStep(nextVisible, formAnswers, "forward", false);
    } else {
      // All remaining steps are hidden — treat as funnel complete and trigger matching
      logger.info(
        "[FormFunnelPage] All remaining steps are conditionally hidden, triggering matching"
      );
      setFlowState("loading");
      void handleGetMatches(formAnswers);
    }
  }, [
    flowState,
    isHydrated,
    currentStep,
    totalSteps,
    isStepVisible,
    findNextVisibleStep,
    formAnswers,
    handleGetMatches,
    navigateQuestionStep,
  ]);

  const handleStepComplete = useCallback(
    (values: Record<string, unknown>) => {
      if (
        flowStateRef.current !== "questions" ||
        questionNavigationRef.current.step !== currentStep ||
        questionNavigationRef.current.generation !== questionGeneration
      ) {
        return;
      }
      const currentFunnelStep = funnelSteps[currentStep];
      if (!currentFunnelStep) return;
      // Consume this rendered action before advancing. Stale/double callbacks
      // cannot submit again; a deliberate Back navigation creates a new action.
      questionNavigationRef.current.generation += 1;
      const isInterstitial = currentFunnelStep?.type === "interstitial";

      // Use formAnswersRef to always read the latest formAnswers, avoiding the stale
      // closure bug during rapid auto-advance (radio/pictureChoice questions that
      // auto-submit after 300ms). Without the ref, the second answer would overwrite
      // the first because the closure captured a stale formAnswers snapshot.
      const updatedAnswers = isInterstitial
        ? formAnswersRef.current
        : { ...formAnswersRef.current, ...normalizeFunnelAnswers(values) };
      if (!isInterstitial) {
        setFormAnswers(updatedAnswers);
      }

      // Track step completion
      trackStepComplete(`Step ${currentStep + 1}`, currentStep);

      // Skip disqualification and incremental save for interstitials (no data collected)
      if (!isInterstitial && currentFunnelStep?.type === "question") {
        const currentQuestions = currentFunnelStep.questions;

        // Check disqualification rules for ALL questions on this step
        if (funnel.disqualificationConfig?.enabled) {
          for (const stepQuestion of currentQuestions) {
            const answeredKey = stepQuestion.id;
            if (!answeredKey) continue;

            const disqualification = checkDisqualification(
              updatedAnswers,
              answeredKey,
              funnel.disqualificationConfig
            );

            if (disqualification) {
              trackEvent("Form Funnel - Disqualified", {
                ruleName: disqualification.triggeredRule.ruleName,
                questionId: answeredKey,
              });
              setDisqualificationResult(disqualification);
              setFlowState("disqualified");
              return; // Don't advance to next step
            }
          }
        }

        // Incrementally save ALL answered questions on this step
        for (const stepQuestion of currentQuestions) {
          const answeredKey = stepQuestion.id;
          if (!answeredKey) continue;
          const currentQuestion = funnel.questions.find((q) => q.fieldKey === answeredKey);
          const answerValue = updatedAnswers[answeredKey];
          void saveAnswer(answeredKey, answerValue, currentQuestion);
        }
      }

      if (!isPreview && !isAdmin) {
        trackFlowStep(describeFunnelQuestionStep(currentFunnelStep, currentStep, updatedAnswers));
      }

      // Find the next visible step directly (skip hidden steps in one go).
      // Pass updatedAnswers explicitly since setFormAnswers hasn't re-rendered yet.
      const nextVisible = findNextVisibleStep(currentStep, "forward", updatedAnswers);
      if (nextVisible >= 0) {
        navigateQuestionStep(nextVisible, updatedAnswers, "forward");
      } else {
        // All remaining questions answered - go to loading/matching
        setQuestionProgress((previous) =>
          moveQuestionProgress(previous, currentStep, totalSteps, 0, "forward")
        );
        setFlowState("loading");
        void handleGetMatches(updatedAnswers);
      }
    },
    [
      currentStep,
      questionGeneration,
      isPreview,
      isAdmin,
      trackFlowStep,
      funnelSteps,
      funnel.disqualificationConfig,
      funnel.questions,
      findNextVisibleStep,
      handleGetMatches,
      trackStepComplete,
      trackEvent,
      saveAnswer,
      navigateQuestionStep,
      totalSteps,
    ]
  );

  const handleBack = useCallback(() => {
    const prevVisible = findNextVisibleStep(currentStep, "backward", formAnswers);
    if (prevVisible >= 0) {
      navigateQuestionStep(prevVisible, formAnswers, "back");
    }
  }, [currentStep, findNextVisibleStep, formAnswers, navigateQuestionStep]);

  const handleHostedComplete = useCallback(
    (redirectUrl?: string, firstName?: string, hostedContext?: HostedCompletionContext) => {
      const completedHostedScenario =
        hostedContext ?? (matchScenario?.type === "HOSTED" ? matchScenario : null);

      if (completedHostedScenario) {
        setSuccessData({
          programName: completedHostedScenario.program.name,
          providerName: completedHostedScenario.program.provider.fullName,
          providerLogoUrl: completedHostedScenario.program.provider.logoImageFile?.url ?? undefined,
          providerSuccessMessage: completedHostedScenario.program.provider.successPageMessage,
          industry: completedHostedScenario.program.vocation?.industry,
          hasFinancialAid: completedHostedScenario.program.hasFinancialAidOption ?? false,
          firstName,
          successPageConfig: completedHostedScenario.hostedFlowConfig?.successPageConfig,
          redirectUrl,
          redirectDelaySeconds:
            completedHostedScenario.hostedFlowConfig?.postLeadRedirectDelaySeconds,
        });
      }
      setFlowState("success");
    },
    [matchScenario]
  );

  const handleProgressChange = useCallback((update: FunnelProgressUpdate) => {
    if (update.phase === "preMatchHosted" && flowStateRef.current === "preMatchHosted") {
      setPreMatchProgress((previous) => updateProgress(previous, update));
    } else if (update.phase !== "preMatchHosted" && flowStateRef.current === "results") {
      setHostedProgress((previous) => updateProgress(previous, update));
    }
  }, []);

  const handleBackFromFirstHostedStep = useCallback(() => {
    setHostedProgress(FUNNEL_PROGRESS.results);
    navigateQuestionStep(currentStep, formAnswers, "back");
    setFlowState("questions");
  }, [currentStep, formAnswers, navigateQuestionStep]);

  const progressPercent =
    flowState === "results"
      ? hostedProgress
      : flowState === "preMatchHosted"
        ? preMatchProgress
        : flowState === "loading"
          ? postPreMatchContact
            ? FUNNEL_PROGRESS.preMatchComplete
            : FUNNEL_PROGRESS.questionsComplete
          : flowState === "success"
            ? FUNNEL_PROGRESS.complete
            : questionProgress.percent;

  const pageContentConfig = funnel.footerConfig;
  // CSS owns the breakpoint, including the first paint before hydration.
  const isSplitLayout = funnel.desktopLayout === "split";
  const isLandingLayout = funnel.desktopLayout === "landing";
  const landingLoadingStyles = isLandingLayout
    ? ({
        position: "relative",
        top: "auto",
        left: "auto",
        width: "100%",
        height: "auto",
        minHeight: { xs: 280, md: 400 },
        zIndex: 0,
        bgcolor: "transparent",
      } as const)
    : undefined;
  const showFirstStepBanner =
    !isLandingLayout &&
    pageContentConfig?.enabled === true &&
    flowState === "questions" &&
    currentFunnelStep?.type === "question" &&
    currentStep === firstVisibleQuestionStepIndex &&
    Boolean(pageContentConfig?.bannerImage);

  const renderCurrentQuestionStep = () => {
    const step = funnelSteps[currentStep];
    if (step?.type === "interstitial") {
      return (
        <InterstitialStep
          content={step.source.interstitialContent}
          image={step.source.interstitialImage}
          onContinue={() => handleStepComplete({})}
          onBack={visibleCurrentStepIndex > 0 ? handleBack : undefined}
        />
      );
    }
    if (isLoadingDynamicOptions) {
      return (
        <LoadingScreen data-testid="funnel-dynamic-options-loading" sx={landingLoadingStyles} />
      );
    }
    return (
      <DynamicFormStep
        stepId={`${funnel.id}:${currentStep}:${currentDynamicQuestions.map((question) => question.id).join("|")}`}
        restorationReady={isHydrated}
        questions={step?.type === "question" ? currentDynamicQuestions : []}
        onComplete={handleStepComplete}
        onBack={visibleCurrentStepIndex > 0 ? handleBack : undefined}
        savedAnswers={currentDynamicAnswers}
        engagement={engagement}
        trustBadgeText={currentStep === firstVisibleQuestionStepIndex ? trustBadgeText : undefined}
        headline={
          !isLandingLayout && currentStep === firstVisibleQuestionStepIndex
            ? funnel.headerConfig?.title || funnel.title
            : undefined
        }
        subtitle={
          !isLandingLayout && currentStep === firstVisibleQuestionStepIndex
            ? funnel.headerConfig?.subtitle
            : undefined
        }
      />
    );
  };

  return (
    <FormFunnelPageLayout
      funnel={funnel}
      legalLinks={legalLinks}
      progressPercent={progressPercent}
      bannerOffset={bannerOffset}
      showHeader={flowState !== "success"}
      showCenteredFooter={!["loading", "success"].includes(flowState)}
      showFirstStepBanner={showFirstStepBanner}
    >
      {flowState === "questions" && (
        <FormFunnelStepLayout
          config={pageContentConfig}
          showBanner={showFirstStepBanner}
          pageOwnsContent={isSplitLayout || isLandingLayout}
          inline={isLandingLayout}
        >
          {renderCurrentQuestionStep()}
        </FormFunnelStepLayout>
      )}

      {flowState === "loading" && (
        <LoadingScreen
          data-testid="funnel-loading-screen"
          sx={
            isLandingLayout
              ? landingLoadingStyles
              : isSplitLayout
                ? {
                    position: { md: "relative" },
                    width: { md: "100%" },
                    height: { md: "auto" },
                    minHeight: { md: "60vh" },
                  }
                : undefined
          }
          // First-pass loading keeps the original copy. Only the
          // post-contact pass overrides — users who never hit the
          // pre-match gate see no UX change. ENG-6248.
          message={postPreMatchContact ? "Verifying your match..." : undefined}
        />
      )}

      {flowState === "preMatchHosted" && (
        <Box sx={{ mt: 4 }}>
          <PreMatchHostedFlow
            onComplete={handlePreMatchContactComplete}
            funnelId={funnel.id}
            formFunnelSessionId={formFunnelSessionId}
            isPreview={isPreview}
            onProgressChange={handleProgressChange}
          />
        </Box>
      )}

      {flowState === "results" && matchScenario && (
        <ResultScenarioRouter
          scenario={matchScenario}
          clickoutConfig={funnel.resultScenarios?.clickoutConfig}
          isPreview={isPreview}
          funnelId={funnel.id}
          formFunnelSessionId={formFunnelSessionId}
          answers={formAnswers}
          onHostedComplete={handleHostedComplete}
          onProgressChange={handleProgressChange}
          onBackFromFirstHostedStep={handleBackFromFirstHostedStep}
        />
      )}

      {flowState === "disqualified" && disqualificationResult && (
        <Box sx={{ mt: 4 }}>
          <DisqualifiedStep
            config={disqualificationResult.resultConfig}
            onChangeAnswer={() => {
              // Navigate back to the step containing the target question
              const targetKey = disqualificationResult.resultConfig.changeAnswerTargetQuestion;
              if (targetKey) {
                const targetStepIndex = funnelSteps.findIndex(
                  (s) => s.type === "question" && s.questions.some((q) => q.id === targetKey)
                );
                if (targetStepIndex >= 0) {
                  navigateQuestionStep(targetStepIndex, formAnswers, "back");
                } else {
                  logger.warn(
                    `[FormFunnelPage] changeAnswerTargetQuestion "${targetKey}" not found in any step`
                  );
                }
                setDisqualificationResult(null);
                setFlowState("questions");
              } else {
                // Fallback: go back to previous visible step
                const prevVisible = findNextVisibleStep(currentStep, "backward", formAnswers);
                if (prevVisible >= 0) {
                  navigateQuestionStep(prevVisible, formAnswers, "back");
                }
                setDisqualificationResult(null);
                setFlowState("questions");
              }
            }}
            onRedirect={() => {
              const redirectUrl = disqualificationResult.resultConfig.redirectUrl;
              if (
                redirectUrl &&
                (redirectUrl.startsWith("/") || /^https?:\/\//.test(redirectUrl))
              ) {
                window.location.href = redirectUrl;
              } else if (redirectUrl) {
                logger.warn(
                  `[FormFunnelPage] Blocked potentially unsafe redirect URL: ${redirectUrl}`
                );
              }
            }}
          />
        </Box>
      )}

      {flowState === "success" && successData && (
        <SuccessPage
          programName={successData.programName}
          providerName={successData.providerName}
          providerLogoUrl={successData.providerLogoUrl}
          providerMessage={successData.providerSuccessMessage}
          industry={successData.industry}
          hasFinancialAid={successData.hasFinancialAid}
          firstName={successData.firstName}
          config={successData.successPageConfig}
          redirectUrl={successData.redirectUrl}
          redirectDelaySeconds={successData.redirectDelaySeconds}
        />
      )}
    </FormFunnelPageLayout>
  );
};

export const FormFunnelPage = ({
  funnel,
  legalLinks,
  trustBadgeText,
  isPreview = false,
  experimentAttribution = [],
  isFreshAssignment = false,
  experimentName,
  createMarketplaceEventOnMount = false,
}: FormFunnelPageProps) => {
  const {
    role: { isAdmin },
  } = useAuthenticatedContext();

  const questionnaireStepCount = useMemo(
    () =>
      buildFunnelSteps({ questions: funnel.questions, questionConfig: funnel.questionConfig })
        .length,
    [funnel.questions, funnel.questionConfig]
  );

  useEffect(() => {
    setCookie(COOKIE_KEYS.FORM_FUNNEL_ID, funnel.id, { expires: 1800 / 86400 });
  }, [funnel.id]);

  return (
    <FormFunnelTrackingProvider
      trackingConfig={{
        // First entry of the ordered programIds list is the funnel's linked
        // program (hasMany text; skip any empty entries defensively).
        programId: funnel.trackingConfig?.programIds?.find((id) => id) ?? null,
        funnelSlug: funnel.slug,
        funnelName: funnel.name || funnel.title,
        enableTrustedForm: funnel.trackingConfig?.enableTrustedForm ?? false,
        facebookPixelId: funnel.trackingConfig?.facebookPixelId ?? null,
        funnelExperiments: experimentAttribution,
        isFreshAssignment,
        experimentName,
        createMarketplaceEventOnMount,
        isPreview,
        questionnaireStepCount,
        hostedFlowSteps: funnel.hostedFlowConfig?.steps,
      }}
    >
      {isAdmin && <AdminBanner />}
      {isPreview && !isAdmin && <PreviewBanner slug={funnel.slug} />}
      <FormFunnelContent
        funnel={funnel}
        legalLinks={legalLinks}
        trustBadgeText={trustBadgeText}
        isPreview={isPreview}
      />

      {/* Facebook Pixel Script - Only render if pixel ID is valid (15-16 digit number) */}
      {funnel.trackingConfig?.facebookPixelId &&
        isValidFacebookPixelId(funnel.trackingConfig.facebookPixelId) && (
          <Script
            id="fb-pixel"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${funnel.trackingConfig.facebookPixelId}');
              fbq('track', 'PageView');
            `,
            }}
          />
        )}
    </FormFunnelTrackingProvider>
  );
};
